// src/components/email/EmailTemplateList.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import Swal from 'sweetalert2';

function EmailTemplateList() {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargarPlantillas();
  }, []);

  const cargarPlantillas = async () => {
    try {
      const res = await api.get('/email/plantillas');
      setPlantillas(res.data.plantillas || []);
    } catch (error) {
      console.error('Error cargando plantillas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async (id, nombre) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar Plantilla?',
      html: `¿Estás seguro de eliminar la plantilla <strong>${nombre}</strong>?<br><br><span style="color: #ef4444;">⚠️ Esta acción no se puede deshacer</span>`,
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/email/plantillas/${id}`);

      Swal.fire({
        icon: 'success',
        title: '✅ Eliminada',
        text: 'La plantilla ha sido eliminada correctamente',
        timer: 2000,
        showConfirmButton: false
      });

      cargarPlantillas();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.error || 'No se pudo eliminar la plantilla'
      });
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Cargando plantillas...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>📄 Plantillas de Email</h1>
        <Link to="/email/plantillas/nueva" style={styles.btnPrimary}>
          <span>➕</span> Nueva Plantilla
        </Link>
      </div>

      {/* Grid de plantillas */}
      <div style={styles.templatesGrid}>
        {plantillas.length > 0 ? (
          plantillas.map(plantilla => (
            <div key={plantilla.id} style={styles.templateCard}>
              <div style={styles.templateHeader}>
                <div style={styles.templateIcon}>📧</div>
                <div style={styles.templateBadge}>
                  {plantilla.activo ? '✅ Activa' : '❌ Inactiva'}
                </div>
              </div>
              <h3 style={styles.templateTitle}>{plantilla.nombre}</h3>
              <p style={styles.templateSubject}>{plantilla.asunto}</p>
              <div style={styles.templateVariables}>
                <span style={styles.variableLabel}>Variables:</span>
                <div style={styles.variableList}>
                  {(plantilla.variables || []).slice(0, 5).map((v, i) => (
                    <span key={i} style={styles.variable}>{`{{${v}}}`}</span>
                  ))}
                </div>
              </div>
              
              {/* Botones de Acción */}
              <div style={styles.templateActions}>
                <Link 
                  to={`/email/plantillas/editar/${plantilla.id}`}
                  style={styles.btnEdit}
                >
                  ✏️ Editar
                </Link>
                <button 
                  onClick={() => handleEliminar(plantilla.id, plantilla.nombre)}
                  style={styles.btnDelete}
                >
                  🗑️ Eliminar
                </button>
              </div>
            </div>
          ))
        ) : (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📄</div>
            <h3 style={styles.emptyTitle}>No hay plantillas</h3>
            <p style={styles.emptyText}>Crea tu primera plantilla para comenzar</p>
            <Link to="/email/plantillas/nueva" style={styles.btnPrimary}>
              Crear Plantilla
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS
// ═══════════════════════════════════════════════════════════════════════════
const styles = {
  container: {
    minHeight: 'calc(100vh - 70px)',
    background: 'linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
    padding: '24px 32px',
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#64748b'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  btnPrimary: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
    transition: 'all 0.2s'
  },
  templatesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '20px'
  },
  templateCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '2px solid #e5e7eb',
    transition: 'all 0.2s'
  },
  templateHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  templateIcon: {
    fontSize: '32px'
  },
  templateBadge: {
    padding: '6px 12px',
    background: '#d1fae5',
    color: '#065f46',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600'
  },
  templateTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '8px'
  },
  templateSubject: {
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '16px',
    fontStyle: 'italic'
  },
  templateVariables: {
    paddingTop: '16px',
    borderTop: '1px solid #e5e7eb'
  },
  variableLabel: {
    fontSize: '12px',
    color: '#64748b',
    fontWeight: '600',
    display: 'block',
    marginBottom: '8px'
  },
  variableList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  variable: {
    padding: '4px 10px',
    background: '#dbeafe',
    color: '#1e40af',
    borderRadius: '12px',
    fontSize: '12px',
    fontFamily: 'monospace',
    fontWeight: '600'
  },
  templateActions: {
    display: 'flex',
    gap: '10px',
    marginTop: '20px',
    paddingTop: '20px',
    borderTop: '1px solid #e5e7eb'
  },
  btnEdit: {
    flex: 1,
    padding: '10px 16px',
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    color: 'white',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '600',
    fontSize: '14px',
    textDecoration: 'none',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  btnDelete: {
    flex: 1,
    padding: '10px 16px',
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: 'white',
    borderRadius: '8px',
    border: 'none',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  emptyState: {
    gridColumn: '1 / -1',
    textAlign: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '16px',
    opacity: 0.5
  },
  emptyTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '8px'
  },
  emptyText: {
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '24px'
  }
};

export default EmailTemplateList;